import React, { useImperativeHandle, useRef, forwardRef } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { FormColumn } from '#components/TableForm';
import { Empty, Table } from '@bedrock/components';
import NoFiles from '@bedrock/components/es/Empty/images/small/NoFiles'
import { calculateTimeConsuming } from '#features/utils';
import OvertimeTag from '#features/OvertimeTag';
import { financial } from 'utils/number';
import CurrencyType from '#components/SForm/SFormElement/CurrencyType';
import TextShow from '#components/SForm/SFormElement/TextShow';
import { useFilterValues, FilterForm, useColumns } from './useFilterValues';

import "./index.less"

type Props = {
    value?: any[];
    onChange?: (value: any[]) => void;
    className?: string;
    view?: boolean;
}

const Index = (props: Props, ref) => {
    const { className, view = false } = props
    const tableFormRef = useRef(null)
    const { filterValues, showValue, onFinish, handleReset, optionsMap } = useFilterValues(props);
    const { fakeKey, createdAt } = useColumns(view);
    useImperativeHandle(ref, () => {
        return {
            validateFields: () => {
                return tableFormRef.current?.form?.validateFields()
                    .catch((error) => {
                        const errMessage = error?.errorFields?.[0]?.errors?.[0]
                        return Promise.reject(errMessage)
                    })
            },
            form: tableFormRef.current?.form
        }
    }, [])

    const columns: FormColumn[] = [
        fakeKey,
        // 作品链接 - 固定在左侧，400px
        {
            title: '作品链接',
            dataIndex: 'work_url',
            width: '280px' ,
            required: true,
            align: 'left',
            fixed: 'left',
            render: (text) => (
                <div className='view-input-work_url'>
                    <TextShow
                        tipToolTitle="复制"
                        style={{ cursor: 'pointer', color: '#007AFF' }}
                        onClick={() => { text ? window.open(text, '_blank') : null }}
                        text={text}
                        ableCopy={true}
                        textShowTitle={text}
                        />
                </div>
            )
        },
        // 平台 - 120px
        {
            title: '平台',
            dataIndex: 'platform',
            width: view ? '80px' : '120px',
            required: true,
            align: 'left',
            render: (text) => {
                const options = optionsMap?.['kol_platform']?.options || [];
                const platformItem = options.find(item => item?.value == text);
                return <TextShow text={platformItem?.name} textShowTitle={text} />
            }
        },
        // 作者ID - 160px
        {
            title: '作者ID',
            dataIndex: 'kol_code',
            width: '160px',
            required: true,
            align: 'left',
            render: (text) => (
                <div className='view-input-work_url'>
                    <TextShow text={text} ableCopy={true} textShowTitle={text} tipToolTitle="复制" />
                </div>
            )
        },
        // 昵称 - 160px
        {
            title: '昵称',
            dataIndex: 'kol_name',
            width: view ? '120px' : '160px',
            required: true,
            align: 'left',
            render: (text) => (
                <div className='view-input-work_url'>
                    <TextShow text={text} textShowTitle={text} />
                </div>
            )
        },
        // 加热费用 - 264px
        {
            title: '加热费用',
            dataIndex: 'boost_cost',
            width: view ? '200px' : '264px',
            required: true,
            align: 'right',
            render: (text, record, index) => {
                return <CurrencyType view value={text} currencyColor='black' />
            },
        },
        // 加热效果播放量 - 160px
        {
            title: '加热效果播放量',
            dataIndex: 'boost_view_count',
            width: view ? '132px' : '160px',
            required: true,
            align: 'right',
            render: (text, record, index) => {
                return <div className='fw-bold'>{financial(text, true)}</div>
            },
        },
        // 加热开始时间 - 200px
        {
            title: '加热开始时间',
            dataIndex: 'boost_start_time',
            width: view ? '160px' : '200px',
            required: true,
            align: 'left',
        },
        // 加热结束时间 - 200px
        {
            title: '加热结束时间',
            dataIndex: 'boost_end_time',
            width: view ? '160px' : '200px',
            required: true,
            align: 'left',
        },
        // 加热链接上传时间 - 180px
        createdAt,
        // 加热链接上传用时 - 160px，固定在右侧
        {
            title: '加热链接上传用时',
            dataIndex: 'boost_upload_time',
            width: '160px',
            align: 'left',
            fixed: 'right',
            render: (text, record, index) => {
                if (!record?.id) {
                    return '--'
                }
                // 上传时间
                const created_at = moment(record?.created_at).valueOf()
                // 加热结束时间
                const boost_end_time = moment(record?.boost_end_time).valueOf()
                const time = created_at - boost_end_time
                const value = calculateTimeConsuming(time);
                return (
                    <div>
                        <div>{value}</div>
                        <div className='m-t-4'>
                            <OvertimeTag 
                                delay_grade={record?.delay_grade} 
                                delay_label={record.delay_label} 
                                className="m-l-0" 
                                tagCompColorLight 
                            />
                        </div>
                    </div>
                )
            }
        },
    ].filter(Boolean) as any

    return (
        <div className={className}>
            <FilterForm onFinish={onFinish} handleReset={handleReset} filterValues={filterValues} optionsMap={optionsMap} />
            <Table
                ref={tableFormRef}
                scroll={{ x: 'max-content', y: 'calc(100vh - 230px)' }}
                className='boost-table-form'
                emptyText={<Empty imageNode={<NoFiles />} text="暂无数据" />}
                data={showValue}
                rowKey={(record) => record?.id}
                columns={columns}
                pagination={{
                    pageSizeOptions: [10, 20, 50, 100],
                    defaultPageSize: 20,
                    showSizeChanger: true,
                    isShowTotal: true,
                    hideOnSinglePage: true,
                    total: showValue?.length || 0,
                }}
            />
        </div>
    )
}

export default forwardRef(Index)           